import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  InputSignal,
  Signal,
  TemplateRef,
  afterRenderEffect,
  contentChild,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import Swiper from 'swiper';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { InsightsEventsTrackingService } from '@core/shared/util';

import { CarouselInsightsEventsConfig } from './carousel-insights-config';
import { CarouselElements, getCarouselConfig } from './carousel-utils';

@Component({
  selector: 'mp-carousel',
  templateUrl: './carousel.component.html',
  styleUrl: './carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, MatIcon],
})
export class CarouselComponent {
  private readonly insightsEventsTrackingService: InsightsEventsTrackingService | null = inject(
    InsightsEventsTrackingService,
    { optional: true },
  );

  readonly customCarouselConfig: InputSignal<SwiperOptions | undefined> = input<SwiperOptions>();

  readonly insightsEventConfig: InputSignal<CarouselInsightsEventsConfig | undefined> =
    input<CarouselInsightsEventsConfig>();

  readonly slidesData: InputSignal<unknown[]> = input<unknown[]>([]);

  protected readonly swiperContainer: Signal<ElementRef<HTMLElement>> =
    viewChild.required<ElementRef<HTMLElement>>('swiperContainer');

  protected readonly navigationPrev: Signal<ElementRef<HTMLElement>> =
    viewChild.required<ElementRef<HTMLElement>>('navigationPrev');
  protected readonly navigationNext: Signal<ElementRef<HTMLElement>> =
    viewChild.required<ElementRef<HTMLElement>>('navigationNext');

  protected readonly scrollbar: Signal<ElementRef<HTMLElement>> =
    viewChild.required<ElementRef<HTMLElement>>('scrollbar');

  protected readonly slideTemplate: Signal<TemplateRef<unknown>> = contentChild.required(TemplateRef);

  private readonly NAVIGATION_DISABLED_CLASS: string = 'carousel-container__navigation--disabled';

  private swiper: Swiper | undefined = undefined;

  constructor() {
    afterRenderEffect(() => {
      this.initCarousel(this.customCarouselConfig());
    });
  }

  private initCarousel(carouselConfig: SwiperOptions | undefined) {
    if (this.swiper) this.swiper.destroy();

    this.swiper = new Swiper(
      this.swiperContainer().nativeElement,
      getCarouselConfig(carouselConfig, this.getCarouselElements()),
    );

    this.handleCarouselInsightsEvents(this.swiper);
  }

  private getCarouselElements(): CarouselElements {
    return {
      navigationDisabledClass: this.NAVIGATION_DISABLED_CLASS,
      navigationNext: this.navigationNext().nativeElement,
      navigationPrev: this.navigationPrev().nativeElement,
      scrollbar: this.scrollbar().nativeElement,
    };
  }

  private handleCarouselInsightsEvents(swiper: Swiper): void {
    if (!this.insightsEventConfig()) return;

    swiper.on('navigationNext', () => this.trackNavigation());

    swiper.on('navigationPrev', () => this.trackNavigation());

    swiper.on('scrollbarDragEnd', () => this.trackNavigation());
  }

  private trackNavigation(): void {
    const insightsEventConfig: CarouselInsightsEventsConfig | undefined = this.insightsEventConfig();

    if (insightsEventConfig) {
      this.insightsEventsTrackingService?.trackEvent(insightsEventConfig.navigationUsage);
    }
  }
}
